import React, { Component } from 'react';
import { CSSTransition } from 'react-transition-group';

import Dialog from 'components/Dialog';
import { RadioButtonCell } from 'components/RadioButton';
import { compareTime, parseTimeString, serialize24HoursTime } from 'core/datetime';
import { weekDays, weekNumberToString } from 'globals/constants';
import { pluralize } from 'utils';

import { ReactComponent as ErrorIcon } from 'assets/warning.svg';

import TimePicker from '../TimePicker';
import { Button, TextField } from '../UtilElements';
import css from './Course.module.scss';
import { convertHoursToMinutes, convertMinutesToHoursAndMinutes } from './helper';

interface Schedule {
  day: WeekDayNumber;
  startTime: TimeIn24Hours;
  endTime: TimeIn24Hours;
}

interface ScheduleConflict {
  target: Schedule;
  conflictsWith: Schedule[];
}

interface CreateWeeklyClassDialogProps {
  isOpen: boolean;
  classeSchedules: CourseScheduleData[];
  onDone(schedule: ClassSchedule): void;
  onCancel(): void;
}

interface CreateWeeklyClassDialogState extends Omit<ClassSchedule, 'isOnlineMeeting'> {
  isOnlineMeeting: null | NumericBoolean;
  isToInconvenient: boolean;
  isFromInconvenient: boolean;
  isInvalidToTime: boolean;
  existingSchedules: Schedule[];
  conflicts: ScheduleConflict[];
}

const MINUTES_IN_ONE_DAY = 1440;

class CreateWeeklyClassDialog extends Component<CreateWeeklyClassDialogProps, CreateWeeklyClassDialogState> {
  state: CreateWeeklyClassDialogState = {
    from: { hours: 10, minutes: 0 },
    to: { hours: 10, minutes: 50 },
    classType: 'lecture',
    repeatsOn: [],
    isOnlineMeeting: null,
    venue: '',
    isInvalidToTime: false,
    isToInconvenient: false,
    isFromInconvenient: false,
    existingSchedules: [],
    conflicts: [],
  };

  private init() {
    const initialState: CreateWeeklyClassDialogState = {
      from: { hours: 10, minutes: 0 },
      to: { hours: 10, minutes: 50 },
      classType: 'lecture',
      repeatsOn: [],
      isOnlineMeeting: null,
      venue: '',
      isInvalidToTime: false,
      isToInconvenient: false,
      isFromInconvenient: false,
      existingSchedules: this.props.classeSchedules.map((s) => ({
        day: s.day,
        startTime: parseTimeString(s.startTime),
        endTime: parseTimeString(s.endTime),
      })),
      conflicts: [],
    };
    this.setState(initialState);
  }

  public componentDidMount() {
    this.init();
  }

  public componentDidUpdate(prevProps: CreateWeeklyClassDialogProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.init();
    }
  }

  isDaySelected = (day: WeekDay) => {
    return this.state.repeatsOn.indexOf(day.number) > -1;
  };

  isTimeInconvenient(time: TimeIn24Hours) {
    const lowThresholdMinutes = 8 * 60;
    const highThresholdMinutes = 20 * 60;
    const minutes = time.hours * 60 + time.minutes;
    return minutes < lowThresholdMinutes || minutes > highThresholdMinutes;
  }

  isInvalidToTime = (from: TimeIn24Hours, to: TimeIn24Hours) => {
    const toTimeMinutes = to.hours * 60 + to.minutes;
    const fromTimeMinutes = from.hours * 60 + from.minutes;
    return fromTimeMinutes >= toTimeMinutes;
  };

  updateFromTime = (from: TimeIn24Hours) => {
    const add50min = convertHoursToMinutes(from) + 50;

    let updateTime: TimeIn24Hours | null = null;

    if (add50min >= MINUTES_IN_ONE_DAY) {
      updateTime = {
        hours: 23,
        minutes: 59,
      };
      this.setState({
        to: updateTime,
      });
    } else {
      updateTime = convertMinutesToHoursAndMinutes(add50min);
      this.setState({
        to: updateTime,
      });
    }

    const isInvalidToTime = this.isInvalidToTime(from, this.state.to);
    const isFromInconvenient = this.isTimeInconvenient(from);

    this.updateToTime(updateTime);
    this.setState({ from, isInvalidToTime, isFromInconvenient });
  };

  updateToTime = (to: TimeIn24Hours) => {
    const isInvalidToTime = this.isInvalidToTime(this.state.from, to);
    const isToInconvenient = this.isTimeInconvenient(to);
    this.setState({ to, isInvalidToTime, isToInconvenient });
  };

  onupdateVenue = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ venue: (e.target as HTMLInputElement).value });
  };

  onDayToggle = (day: WeekDay) => {
    if (this.isDaySelected(day)) {
      this.setState({
        repeatsOn: this.state.repeatsOn.filter((d) => d !== day.number),
      });
    } else {
      this.setState({
        repeatsOn: [...this.state.repeatsOn, day.number],
      });
    }
  };

  isInValid = () => {
    const { isOnlineMeeting, venue, repeatsOn, isInvalidToTime: endTimeError } = this.state;

    if (isOnlineMeeting == null) return true;

    if (isOnlineMeeting === 0 && !venue.length) return true;

    return !repeatsOn.length || Boolean(endTimeError);
  };

  checkConflicts = () => {
    const scheduleConflicts: ScheduleConflict[] = [];

    const schedules: Schedule[] = this.state.repeatsOn.map((d) => ({
      day: d,
      startTime: this.state.from,
      endTime: this.state.to,
    }));

    schedules.forEach((newSchedule) => {
      /**
       * All possible cases for schedule overlaps.
       * schedule:                --------             // exisiting schedule, rest are new schedule cases
       * case1:               --------
       * case2:               ----------------
       * case3:                     ----
       * case4:                     ----------
       * case5:      --------
       * case6:                                --------
       * In all above cases case5 and case6 do not conflicts
       */
      const conflicts = this.state.existingSchedules.filter((existingSchedule) => {
        const isSameDay = existingSchedule.day === newSchedule.day;
        const isOverLapping = !(
          compareTime(newSchedule.endTime, '<', existingSchedule.startTime) ||
          compareTime(existingSchedule.endTime, '<', newSchedule.startTime)
        );
        return isSameDay && isOverLapping;
      });

      if (conflicts.length) {
        scheduleConflicts.push({ target: newSchedule, conflictsWith: conflicts });
      }
    });

    this.setState({ conflicts: scheduleConflicts });
  };

  onDone = () => {
    this.checkConflicts();

    if (this.state.conflicts.length) return;

    this.props.onDone({
      classType: this.state.classType,
      from: this.state.from,
      to: this.state.to,
      venue: this.state.venue,
      repeatsOn: this.state.repeatsOn,
      isOnlineMeeting: this.state.isOnlineMeeting!,
    });
  };

  dayDetails() {
    return (
      <div className='weeklyclass__row'>
        <div>Sessions happen every</div>
        <div className='weeklyclass__pill--wrap'>
          {weekDays.map((day) => (
            <span
              key={day.number}
              onClick={() => this.onDayToggle(day)}
              className={`weeklyclass__pill ${this.isDaySelected(day) ? 'selected' : ''}`}
            >
              {day.label.toUpperCase()}
            </span>
          ))}
        </div>
      </div>
    );
  }

  fromDetails() {
    const { from, isFromInconvenient } = this.state;

    return (
      <div className='weeklyclass__row'>
        <p>From (Start Time)</p>
        <TimePicker value={from} onChange={this.updateFromTime} />
        {isFromInconvenient ? (
          <div className='weeklyclass__error form-field--warning'>
            <ErrorIcon />
            <span>This time seems a little inconvenient</span>
          </div>
        ) : null}
      </div>
    );
  }

  toDetails() {
    const { to, isInvalidToTime, isToInconvenient } = this.state;

    return (
      <div className={'weeklyclass__row'}>
        <p>Till (End Time)</p>
        <TimePicker value={to} onChange={this.updateToTime} />
        {isInvalidToTime ? (
          <div className='weeklyclass__error form-field--error'>
            <ErrorIcon />
            <span>Class end time should be after the class start time</span>
          </div>
        ) : isToInconvenient ? (
          <div className='weeklyclass__error form-field--warning'>
            <ErrorIcon />
            <span>This time seems a little inconvenient</span>
          </div>
        ) : null}
      </div>
    );
  }

  onlineDetails() {
    const { isOnlineMeeting } = this.state;
    return (
      <>
        <div className='weeklyclass__section-heading'>Select class modality</div>
        <RadioButtonCell
          className='weeklyclass__radio-cell'
          isSelected={isOnlineMeeting === 1}
          onToggle={() => this.setState({ isOnlineMeeting: isOnlineMeeting === 1 ? null : 1 })}
        >
          Online on Acadly (with Zoom plugin)
        </RadioButtonCell>
        <RadioButtonCell
          className='weeklyclass__radio-cell'
          isSelected={isOnlineMeeting === 0}
          onToggle={() => this.setState({ isOnlineMeeting: isOnlineMeeting === 0 ? null : 0 })}
        >
          <span>In-person (or on another online platform)</span>
        </RadioButtonCell>
      </>
    );
  }

  venueDetails() {
    const { isOnlineMeeting } = this.state;

    return (
      <CSSTransition in={isOnlineMeeting === 0} classNames='fade' timeout={250} unmountOnExit>
        <div className='weeklyclass__row'>
          <span>At (venue/link)</span>
          <TextField
            value={this.state.venue}
            onchange={this.onupdateVenue}
            placeholder='Type venue/link here'
            style={{
              flex: 1,
              fontSize: '16px',
              maxWidth: '70%',
              textAlign: 'left',
            }}
          />
        </div>
      </CSSTransition>
    );
  }

  scheduleToString = (s: Schedule) => {
    return `${weekNumberToString[s.day]} ${serialize24HoursTime(s.startTime)} - ${serialize24HoursTime(
      s.endTime
    )}`;
  };

  render() {
    const { conflicts } = this.state;
    return (
      <Dialog
        isOpen={this.props.isOpen}
        classes={{ body: 'weeklyclass' }}
        title='Adding a weekly class schedule'
        onClose={this.props.onCancel}
        footer={
          <>
            <Button
              variant='text'
              color='error'
              size='small'
              onClick={this.props.onCancel}
              preventHoverStyles
            >
              CANCEL
            </Button>
            <Button
              variant='text'
              color='success'
              size='small'
              onClick={this.onDone}
              disabled={this.isInValid()}
              preventHoverStyles
            >
              DONE
            </Button>
          </>
        }
      >
        {this.dayDetails()}
        {this.fromDetails()}
        {this.toDetails()}
        {this.onlineDetails()}
        {this.venueDetails()}
        {conflicts.length ? (
          <div className='weeklyclass__conflict'>
            <div className={css['section-subtitle-text']}>
              Please ensure that the class end time is after the class start time
            </div>
            {conflicts.map((c, i) => (
              <p key={i} className='form-field--error'>
                <ErrorIcon />
                <span>
                  <strong>{this.scheduleToString(c.target)}</strong> clashes with{' '}
                  {pluralize(c.conflictsWith.length, 'lecture')} on{' '}
                  <strong>{c.conflictsWith.map(this.scheduleToString).join(', ')}</strong>
                </span>
              </p>
            ))}
          </div>
        ) : (
          <div className={css['section-subtitle-text']}>
            If there is another session scheduled with different start/end times or a different venue, you can
            add another schedule
          </div>
        )}
      </Dialog>
    );
  }
}

export default CreateWeeklyClassDialog;
